import React, { useCallback, useEffect, useRef, useState } from "react";
import { clampPosition } from "./gradientCss";
import * as S from "./GradientBuilder.styled";

/**
 * Draggable gradient stop-track — the intuitive / "pro" layer that sits ABOVE
 * the numeric stop list in the Custom Gradient builder.
 *
 * This component is purely PRESENTATIONAL + interaction: it never owns gradient
 * state. The parent (`GradientBuilder`) owns the single `stops` array and feeds
 * it down here; every gesture calls back up to mutate that SAME state object.
 * That is what makes the track and the numeric list two-way synced — editing a
 * number moves the handle (state flows down) and dragging a handle updates the
 * number (callbacks flow up). One source of truth, two editors.
 *
 * Interactions
 *  - Click empty bar  -> onAddStopAt(position)  (parent interpolates the color)
 *  - Drag a handle    -> onMoveStop(index, position)  (live, clamped 0–100)
 *  - Click a handle   -> onSelect(index) + onEditColor(index)  (opens picker)
 *  - ✕ on selected    -> onRemoveStop(index)  (min stops enforced by parent)
 *  - Double-click handle -> onRemoveStop(index)
 *  - Keyboard: focus a handle, ←/→ nudge 1% (Shift = 10%), Enter/Space edit
 *    color, Delete/Backspace remove. Fully operable without a pointer (a11y).
 *
 * Works for every gradient type: for radial/conic the bar previews the gradient
 * and the handles still control each stop's POSITION along it, which is correct.
 *
 * @param {Array<{color:string,position:number}>} stops current color stops
 * @param {string} gradientCss live gradient string used as the bar background
 * @param {number|null} selectedIndex index of the selected/active handle
 * @param {(index:number) => void} onSelect select a handle (no color edit)
 * @param {(index:number) => void} onEditColor open the color picker for a stop
 * @param {(position:number) => void} onAddStopAt add a stop at a bar position %
 * @param {(index:number, position:number) => void} onMoveStop move a stop
 * @param {(index:number) => void} onRemoveStop remove a stop
 * @param {boolean} canAdd false when at MAX_GRADIENT_STOPS (no add on click)
 * @param {boolean} canRemove false when at MIN_GRADIENT_STOPS (no remove)
 */
const GradientStopTrack = ({
  stops = [],
  gradientCss,
  selectedIndex = null,
  onSelect,
  onEditColor,
  onAddStopAt,
  onMoveStop,
  onRemoveStop,
  canAdd = true,
  canRemove = true,
}) => {
  const barRef = useRef(null);
  // The index currently being dragged, or null. Held in a ref so the global
  // pointer handlers (attached during a drag) always see the latest value.
  const dragIndexRef = useRef(null);
  // Distinguish a click (select + edit color) from a drag (move only) so a
  // small accidental movement while clicking doesn't suppress the color picker.
  const movedRef = useRef(false);
  const [draggingIndex, setDraggingIndex] = useState(null);

  /** Convert a clientX within the bar to a clamped position percent (0–100). */
  const xToPosition = useCallback((clientX) => {
    const el = barRef.current;
    if (!el) return 0;
    const rect = el.getBoundingClientRect();
    if (rect.width === 0) return 0;
    const ratio = (clientX - rect.left) / rect.width;
    return clampPosition(Math.round(ratio * 100));
  }, []);

  // ----- Drag lifecycle (pointer events on window so the drag survives the
  // pointer leaving the small handle / bar) ---------------------------------
  useEffect(() => {
    if (draggingIndex === null) return undefined;

    const handleMove = (e) => {
      const idx = dragIndexRef.current;
      if (idx === null) return;
      movedRef.current = true;
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      onMoveStop(idx, xToPosition(clientX));
    };

    const handleUp = () => {
      dragIndexRef.current = null;
      setDraggingIndex(null);
    };

    window.addEventListener("pointermove", handleMove);
    window.addEventListener("pointerup", handleUp);
    window.addEventListener("pointercancel", handleUp);
    return () => {
      window.removeEventListener("pointermove", handleMove);
      window.removeEventListener("pointerup", handleUp);
      window.removeEventListener("pointercancel", handleUp);
    };
  }, [draggingIndex, onMoveStop, xToPosition]);

  const startDrag = (index) => (e) => {
    // Left button / primary pointer only.
    if (e.button != null && e.button !== 0) return;
    e.stopPropagation(); // don't let the bar treat this as an "add" click
    dragIndexRef.current = index;
    movedRef.current = false;
    setDraggingIndex(index);
    if (typeof onSelect === "function") onSelect(index);
  };

  const handleHandleClick = (index) => (e) => {
    e.stopPropagation();
    // A genuine click (no drag movement) opens the color picker for that stop.
    if (!movedRef.current && typeof onEditColor === "function") {
      onEditColor(index);
    }
  };

  const handleBarClick = (e) => {
    // Ignore clicks that originated on a handle (those stopPropagation) and
    // clicks while at the stop cap.
    if (!canAdd) return;
    if (typeof onAddStopAt !== "function") return;
    onAddStopAt(xToPosition(e.clientX));
  };

  const handleKeyDown = (index) => (e) => {
    const step = e.shiftKey ? 10 : 1;
    const current = clampPosition(stops[index]?.position ?? 0);
    switch (e.key) {
      case "ArrowLeft":
      case "ArrowDown":
        e.preventDefault();
        onMoveStop(index, clampPosition(current - step));
        break;
      case "ArrowRight":
      case "ArrowUp":
        e.preventDefault();
        onMoveStop(index, clampPosition(current + step));
        break;
      case "Enter":
      case " ":
        e.preventDefault();
        if (typeof onEditColor === "function") onEditColor(index);
        break;
      case "Delete":
      case "Backspace":
        e.preventDefault();
        if (canRemove && typeof onRemoveStop === "function") {
          onRemoveStop(index);
        }
        break;
      default:
        break;
    }
  };

  return (
    <div>
      <S.TrackWrap>
        <S.TrackBar
          ref={barRef}
          css={gradientCss}
          disabled={!canAdd}
          role="group"
          aria-label="Gradient stop track. Click the bar to add a color stop."
          onClick={handleBarClick}
        >
          {stops.map((stop, index) => {
            const selected = selectedIndex === index;
            return (
              <S.TrackHandle
                key={index}
                type="button"
                position={clampPosition(stop.position)}
                color={stop.color}
                selected={selected}
                aria-label={`Color stop ${index + 1}: ${stop.color} at ${clampPosition(
                  stop.position,
                )} percent. Arrow keys move it; Enter edits color; Delete removes it.`}
                aria-pressed={selected}
                onPointerDown={startDrag(index)}
                onClick={handleHandleClick(index)}
                onDoubleClick={(e) => {
                  e.stopPropagation();
                  if (canRemove && typeof onRemoveStop === "function") {
                    onRemoveStop(index);
                  }
                }}
                onKeyDown={handleKeyDown(index)}
              >
                {selected && (
                  <S.HandleRemove
                    as="span"
                    role="button"
                    tabIndex={-1}
                    aria-label={`Remove color stop ${index + 1}`}
                    aria-disabled={!canRemove}
                    onPointerDown={(e) => e.stopPropagation()}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (canRemove && typeof onRemoveStop === "function") {
                        onRemoveStop(index);
                      }
                    }}
                  >
                    ×
                  </S.HandleRemove>
                )}
              </S.TrackHandle>
            );
          })}
        </S.TrackBar>
      </S.TrackWrap>
      <S.TrackHint>
        {canAdd
          ? "Click the bar to add a stop · drag handles to reposition · click a handle to recolor"
          : "Drag handles to reposition · click a handle to recolor (stop limit reached)"}
      </S.TrackHint>
    </div>
  );
};

export default GradientStopTrack;
